import type { CSSObject } from "@emotion/react";
import { css } from "@emotion/react";
import { vars } from "@imwebme/clay-token";
import type { LegacyTabListProps } from "./TabList.types";

const getPaddingForSquared = (size: LegacyTabListProps<string>["size"]) => {
  switch (size) {
    case "small":
      return `${vars.spacing[1]} ${vars.spacing[3]}`;

    case "large":
      return `${vars.spacing[3]} ${vars.spacing[4]}`;

    // medium
    default:
      return `${vars.spacing[2]} ${vars.spacing[3]}`;
  }
};

const getStyleForUnderLine = (size: LegacyTabListProps<string>["size"]) => {
  switch (size) {
    // underline은 small 을 지원하지 않습니다.
    case "small":
      return {};

    case "large":
      return {
        gap: vars.spacing[4],
        ...vars.semantic.typography["label-large"],
      };

    case "xlarge":
      return {
        gap: vars.spacing[5],
        ...vars.semantic.typography["label-xlarge-bold"],
      };

    case "2xlarge":
      return {
        gap: vars.spacing[6],
        ...vars.semantic.typography["label-3xlarge-bold"],
      };

    // medium
    default:
      return {
        gap: vars.spacing[3],
        ...vars.semantic.typography["label-medium"],
      };
  }
};

const segmentCSSBySize = (size: LegacyTabListProps<string>["size"]): CSSObject => {
  switch (size) {
    case "small":
      return {
        paddingTop: vars.spacing[1],
        paddingBottom: vars.spacing[1],
        minWidth: "54px",
      };

    case "large":
      return {
        paddingTop: vars.spacing["25"],
        paddingBottom: vars.spacing["25"],
        minWidth: "64px",
      };

    // medium
    default:
      return {
        paddingTop: vars.spacing["15"],
        paddingBottom: vars.spacing["15"],
        minWidth: "60px",
      };
  }
};

const segmentActiveColorByType = (type: LegacyTabListProps<string>["type"]) => {
  switch (type) {
    case "raised":
      return vars.semantic.color.text;

    case "outlined":
      return vars.semantic.color.textPrimary;

    // primary, secondary
    default:
      return vars.semantic.color.textOn;
  }
};

export const tabListCSS = ({
  variant,
  size,
  background,
  type,
  fullWidth,
}: Pick<LegacyTabListProps<string>, "variant" | "size" | "background" | "type" | "fullWidth">) => {
  switch (variant) {
    case "segment":
      return css({
        backgroundColor: background ? vars.semantic.color.surfaceSub : "transparent",
        padding: background ? vars.spacing[1] : 0,
        borderRadius: vars.rounded.medium,

        '[role="tab"]': {
          ...vars.semantic.typography["label-medium-bold"],
          ...segmentCSSBySize(size),

          paddingLeft: vars.spacing[3],
          paddingRight: vars.spacing[3],
          gap: vars.spacing[size === "medium" || size === "small" ? 1 : "15"],

          transition: "color 0.3s ease-out",
          color: vars.semantic.color.textSecondary,
          flex: fullWidth ? 1 : "auto",

          '&:not([aria-disabled="true"])[aria-selected="true"]': {
            color: segmentActiveColorByType(type),
          },

          '&:not([aria-selected="true"], :active, [aria-disabled="true"]):hover': {
            color: vars.semantic.color.textSecondaryHover,
          },
        },
      });
    case "squared":
      return css({
        gap: vars.spacing[size === "large" ? "15" : 1],

        '[role="tab"]': {
          ...vars.semantic.typography[`label-${size === "large" ? "large" : "medium"}`],

          flex: fullWidth ? 1 : "auto",

          padding: getPaddingForSquared(size),
          gap: vars.spacing[size === "medium" || size === "small" ? 1 : "15"],

          transition: "background-color 0.3s ease-out",

          '&:not([aria-disabled="true"])[aria-selected="true"]': {
            backgroundColor: vars.semantic.color.actionPrimary,
            color: vars.semantic.color.textInverse,
          },

          '&:not([aria-disabled="true"], [aria-selected="true"]):active': {
            backgroundColor: vars.semantic.color.actionSecondaryPressed,
          },

          '&:not([aria-selected="true"], :active, [aria-disabled="true"]):hover': {
            backgroundColor: vars.semantic.color.actionSecondaryHover,
            '& [data-clay-badge-on="false"]': {
              backgroundColor: vars.semantic.color.actionPrimaryDisabled,
            },
          },

          '&[aria-disabled="true"]': {
            backgroundColor: "transparent",
            color: vars.semantic.color.textDisabled,
            cursor: "not-allowed",
          },

          borderRadius: vars.rounded[size === "small" ? "small" : "medium"],
        },
      });
    // text tab
    default:
      return css({
        gap: vars.spacing[size === "medium" ? 3 : 4],

        ...getStyleForUnderLine(size),

        '[role="tab"]': {
          flex: fullWidth ? 1 : "auto",

          padding: `${vars.spacing[size === "medium" ? "2" : "3"]} 0`,
          gap: vars.spacing[size === "medium" || size === "small" ? 1 : "15"],

          transition: "color 0.3s ease-out",
          color: vars.semantic.color.textSub,

          '&:not([aria-disabled="true"])[aria-selected="true"]': {
            color: vars.semantic.color.text,
          },

          '&:not([aria-selected="true"], :active, [aria-disabled="true"]):hover': {
            color: vars.semantic.color.text,
          },

          '&[aria-disabled="true"]': {
            color: vars.semantic.color.textDisabled,
            cursor: "not-allowed",
          },
        },
      });
  }
};
